import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";
import { Navigation, EffectCoverflow, FreeMode, Thumbs } from "swiper";
import SwiperCore from "swiper";
import { FiShare2, FiVolume2, FiVolumeX, FiArrowLeft } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import axios from "axios";
import Modal from "react-modal";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";


SwiperCore.use([Navigation, EffectCoverflow, FreeMode, Thumbs]);

const ReelCarousel = () => {
    const [reels, setReels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMuted, setIsMuted] = useState(true);
    const [selectedReel, setSelectedReel] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [showFullscreenReel, setShowFullscreenReel] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedImage, setSelectedImage] = useState(0);
    const verticalSwiperRef = useRef(null);
    const params = useParams();
    const navigate = useNavigate();
    const [isMobileView, setIsMobileView] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768); // 768px is typically the breakpoint for md in Tailwind
        };

        // Set initial value
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Clean up
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchReels = async () => {
            try {
                const response = await axios.get(
                    `https://trakky.in:8000/salons/client-image-site/?is_city=true&city=${params?.city}`
                );
                const filteredData = response.data.filter(
                    (item) => item.is_city === true && (item.video || item.client_image)
                );
                setReels(filteredData);
                setLoading(false);

                // If URL has reelId, open that reel directly
                if (params.reelId) {
                    const foundReel = filteredData.find(
                        (r) => r.id.toString() === params.reelId
                    );
                    if (foundReel) {
                        setSelectedReel(foundReel);
                        setShowFullscreenReel(true);
                        setActiveIndex(
                            filteredData.findIndex((r) => r.id.toString() === params.reelId)
                        );
                    }
                }
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchReels();
    }, [params.reelId, params?.city]);

    const handleSlideChange = (swiper) => {
        setActiveIndex(swiper.activeIndex);
    };

    const handleReelClick = (reel) => {
        setSelectedReel(reel);
        setShowFullscreenReel(true);
        navigate(`/${params.city}/salons/reel/${reel.id}`, { replace: true });
    };

    const handleCloseFullscreenReel = () => {
        setShowFullscreenReel(false);
        navigate(`/${params.city}/salons`, { replace: true });
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const handleShare = async () => {
        if (!selectedReel) return;

        const reelUrl = `${window.location.origin}/${params.city}/salons/reel/${selectedReel.id}`;

        try {
            if (navigator.share) {
                await navigator.share({
                    title:
                        selectedReel.service_data?.service_name ||
                        selectedReel.category_data?.category_name,
                    text: "Check out this salon service!",
                    url: reelUrl,
                });
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(reelUrl);
                toast.success("Link copied to clipboard!");
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = reelUrl;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
                toast.success("Link copied to clipboard!");
            }
        } catch (err) {
            console.error("Failed to share:", err);
            toast.error("Failed to share link");
        }
    };

    const handleBookNow = (reel) => {
        const salonName =
            reel.service_data?.salon_name ||
            reel.category_data?.salon_name ||
            "the salon";
        const area = reel.service_data?.area || reel.category_data?.area || "";
        const city = reel.category_data?.city || "Ahmedabad";

        const link = `https://api.whatsapp.com/send?phone=916355167304&text=Can%20I%20know%20more%20about%20Offers%20%26%20salon%20services%20of%20${encodeURIComponent(
            salonName
        )}%2C%20${encodeURIComponent(area)}%2C%20${encodeURIComponent(city)}%3F`;

        window.open(link, "_blank");
    };

    const toggleProductModal = () => {
        setShowProductModal(!showProductModal);
    };

    const productImages = selectedReel
        ? [
            selectedReel.service_data?.service_image ||
            selectedReel.category_data?.category_image,
            selectedReel.client_image,
        ].filter(Boolean)
        : [];

    if (loading)
        return (
            <div className="flex justify-center items-center h-screen">
                Loading...
            </div>
        );
    if (error)
        return (
            <div className="flex justify-center items-center h-screen text-red-500">
                Error: {error}
            </div>
        );
    if (!reels.length)
        return (
            <div className="flex justify-center items-center h-screen">
                No reels found
            </div>
        );

    return (
        <>
            {/* Conditional rendering based on screen size */}
            <div className="w-full">
                {isMobileView ? (
                    // Mobile View - Reel Carousel
                    <>
                        <h1 className="text-xl font-semibold text-start px-4 py-4">
                            Grooming Stories
                        </h1>
                        <div className="flex overflow-x-auto gap-3 px-4 pb-4">
                            {reels.map((reel) => (
                                <div
                                    key={reel.id}
                                    className="relative flex flex-col items-center w-44 flex-shrink-0 cursor-pointer"
                                    onClick={() => handleReelClick(reel)}
                                >
                                    <div className="relative aspect-[9/16] rounded-lg overflow-hidden">
                                        {reel.video ? (
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            >
                                                <source src={reel.video} type="video/mp4" />
                                            </video>
                                        ) : reel.client_image ? (
                                            <img
                                                src={reel.client_image}
                                                alt={
                                                    reel.service_data?.service_name ||
                                                    reel.category_data?.category_name
                                                }
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center text-xs text-center p-1">
                                                No media
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                                            <div className="flex items-start space-x-2">
                                                <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center shrink-0">
                                                    {reel.service_data?.service_image ||
                                                        reel.category_data?.category_image ? (
                                                        <img
                                                            src={
                                                                reel.service_data?.service_image ||
                                                                reel.category_data?.category_image
                                                            }
                                                            alt=""
                                                            className="w-6 h-6 object-contain"
                                                        />
                                                    ) : (
                                                        <div className="text-black text-xs">No image</div>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-white text-xs font-medium mb-1 line-clamp-1">
                                                        {reel.service_data?.service_name ||
                                                            reel.category_data?.category_name ||
                                                            "Untitled"}
                                                    </h3>
                                                    {reel.service_data?.price && (
                                                        <p className="text-white text-sm font-bold">
                                                            ₹ {reel.service_data.price}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    // Desktop View - Coverflow Carousel
                    <div className="w-full max-w-7xl mx-auto px-4 py-6">
                        <h1 className="text-center text-xl sm:text-2xl font-bold mb-6">
                            TRUSTED BY ENTHUSIASTS
                        </h1>
                        <Swiper
                            loop={reels.length > 4}
                            spaceBetween={30}
                            slidesPerView={Math.min(4, reels.length)}
                            effect="coverflow"
                            centeredSlides={true}
                            coverflowEffect={{
                                rotate: 0,
                                stretch: 0,
                                depth: 100,
                                modifier: 2.5,
                                slideShadows: true,
                            }}
                            navigation
                            onSlideChange={handleSlideChange}
                            initialSlide={activeIndex}
                            modules={[EffectCoverflow, Navigation]}
                        >
                            {reels.map((reel) => (
                                <SwiperSlide key={reel.id}>
                                    <div
                                        className="relative aspect-[9/16] rounded-lg overflow-hidden cursor-pointer"
                                        onClick={() => handleReelClick(reel)}
                                    >
                                        {reel.video ? (
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            >
                                                <source src={reel.video} type="video/mp4" />
                                            </video>
                                        ) : reel.client_image ? (
                                            <img
                                                src={reel.client_image}
                                                alt={
                                                    reel.service_data?.service_name ||
                                                    reel.category_data?.category_name
                                                }
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-xs text-center p-1">
                                                No media
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                                            <div className="flex items-start space-x-2">
                                                <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center shrink-0">
                                                    {reel.service_data?.service_image ||
                                                        reel.category_data?.category_image ? (
                                                        <img
                                                            src={
                                                                reel.service_data?.service_image ||
                                                                reel.category_data?.category_image
                                                            }
                                                            alt=""
                                                            className="w-6 h-6 object-contain"
                                                        />
                                                    ) : (
                                                        <div className="text-black text-xs">No image</div>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-white text-xs font-medium mb-1 line-clamp-1">
                                                        {reel.service_data?.service_name ||
                                                            reel.category_data?.category_name ||
                                                            "Untitled"}
                                                    </h3>
                                                    {reel.service_data?.price && (
                                                        <p className="text-white text-sm font-bold">
                                                            ₹ {reel.service_data.price}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
            </div>

            {/* Fullscreen Reel Player */}
            {showFullscreenReel && selectedReel && (
                <div className="fixed inset-0 bg-black z-[999] flex flex-col">
                    {isMobileView ? (
                        // Mobile View - Vertical Swiper
                        <div className="h-full w-full">
                            <Swiper
                                direction="vertical"
                                slidesPerView={1}
                                spaceBetween={0}
                                mousewheel
                                className="h-full w-full"
                                initialSlide={activeIndex}
                                onSlideChange={(swiper) => {
                                    setActiveIndex(swiper.activeIndex);
                                    setSelectedReel(reels[swiper.activeIndex]);
                                    navigate(
                                        `/${params.city}/salons/reel/${reels[swiper.activeIndex].id}`,
                                        { replace: true }
                                    );
                                }}
                                ref={verticalSwiperRef}
                            >
                                {reels.map((reel) => (
                                    <SwiperSlide key={reel.id} className="relative">
                                        <div className="relative w-full h-full flex justify-center items-center">
                                            {reel.video ? (
                                                <video
                                                    src={reel.video}
                                                    autoPlay
                                                    muted={isMuted}
                                                    loop
                                                    playsInline
                                                    className="absolute w-full h-full object-contain bg-black"
                                                />
                                            ) : reel.client_image ? (
                                                <img
                                                    src={reel.client_image}
                                                    alt={
                                                        reel.service_data?.service_name ||
                                                        reel.category_data?.category_name
                                                    }
                                                    className="absolute w-full h-full object-contain"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gray-800 flex items-center justify-center text-white">
                                                    No media available
                                                </div>
                                            )}

                                            {/* Controls */}
                                            <div className="absolute top-4 right-4 z-10 flex gap-2">
                                                <button
                                                    onClick={toggleMute}
                                                    className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                                >
                                                    {isMuted ? (
                                                        <FiVolumeX size={20} />
                                                    ) : (
                                                        <FiVolume2 size={20} />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={handleShare}
                                                    className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                                >
                                                    <FiShare2 size={20} />
                                                </button>
                                                <button
                                                    onClick={handleCloseFullscreenReel}
                                                    className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                                >
                                                    <FiArrowLeft size={20} />
                                                </button>
                                            </div>

                                            {/* Reel Info */}
                                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                                                <div className="flex items-start space-x-3 mb-4">
                                                    <div className="w-12 h-12 bg-white rounded-md flex items-center justify-center shrink-0">
                                                        {reel.service_data?.service_image ||
                                                            reel.category_data?.category_image ? (
                                                            <img
                                                                src={
                                                                    reel.service_data?.service_image ||
                                                                    reel.category_data?.category_image
                                                                }
                                                                alt=""
                                                                className="w-10 h-10 object-contain"
                                                            />
                                                        ) : (
                                                            <div className="text-black text-xs">No image</div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="text-white text-base font-medium mb-1">
                                                            {reel.service_data?.service_name ||
                                                                reel.category_data?.category_name}
                                                        </h3>
                                                        {reel.service_data?.price && (
                                                            <p className="text-white text-lg font-bold">
                                                                ₹ {reel.service_data.price}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={toggleProductModal}
                                                    className="w-full bg-white text-black py-2.5 rounded font-medium"
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    ) : (
                        // Desktop View - Side by Side
                        <div className="flex h-full">
                            {/* Left Side - Video */}
                            <div className="w-1/2 h-full bg-black flex items-center justify-center relative">
                                {selectedReel.video ? (
                                    <video
                                        src={selectedReel.video}
                                        autoPlay
                                        muted={isMuted}
                                        loop
                                        playsInline
                                        className="w-full h-full object-contain"
                                    />
                                ) : selectedReel.client_image ? (
                                    <img
                                        src={selectedReel.client_image}
                                        alt={
                                            selectedReel.service_data?.service_name ||
                                            selectedReel.category_data?.category_name
                                        }
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center text-white">
                                        No media available
                                    </div>
                                )}

                                <div className="absolute top-4 right-4 z-10 flex gap-2">
                                    <button
                                        onClick={toggleMute}
                                        className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                    >
                                        {isMuted ? (
                                            <FiVolumeX size={20} />
                                        ) : (
                                            <FiVolume2 size={20} />
                                        )}
                                    </button>
                                    <button
                                        onClick={handleShare}
                                        className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                    >
                                        <FiShare2 size={20} />
                                    </button>
                                    <button
                                        onClick={handleCloseFullscreenReel}
                                        className="text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                    >
                                        <FiArrowLeft size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Right Side - Product Details */}
                            <div className="w-1/2 h-full bg-white overflow-y-auto p-6">
                                {productImages.length > 0 && (
                                    <>
                                        <div className="w-full">
                                            <img
                                                src={productImages[selectedImage]}
                                                alt={`${selectedReel.service_data?.service_name ||
                                                    selectedReel.category_data?.category_name
                                                    }`}
                                                className="w-full h-64 object-contain mx-auto"
                                            />

                                            {productImages.length > 1 && (
                                                <div className="relative py-4">
                                                    <div
                                                        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory"
                                                        style={{
                                                            scrollbarWidth: "thin",
                                                            scrollbarColor: "#888 transparent",
                                                            msOverflowStyle: "none" /* IE and Edge */,
                                                        }}
                                                    >
                                                        {productImages.map((img, index) => (
                                                            <div
                                                                key={index}
                                                                className={`shrink-0 cursor-pointer w-16 h-16 snap-start ${selectedImage === index
                                                                    ? "border-2 border-black"
                                                                    : "border border-gray-200"
                                                                    }`}
                                                                onClick={() => setSelectedImage(index)}
                                                            >
                                                                <img
                                                                    src={img}
                                                                    alt={`Thumbnail ${index + 1}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Custom scrollbar indicator */}
                                                    <div className="h-1 bg-gray-200 mt-2 rounded-full">
                                                        <div
                                                            className="h-full bg-black rounded-full"
                                                            style={{
                                                                width: `${100 / productImages.length}%`,
                                                                transform: `translateX(${selectedImage * 100}%)`,
                                                                transition: "transform 0.3s ease",
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 mb-6">
                                            <div className="w-16 h-16 border border-black rounded-md flex items-center justify-center">
                                                {selectedReel.service_data?.service_image ||
                                                    selectedReel.category_data?.category_image ? (
                                                    <img
                                                        src={
                                                            selectedReel.service_data?.service_image ||
                                                            selectedReel.category_data?.category_image
                                                        }
                                                        alt={
                                                            selectedReel.service_data?.service_name ||
                                                            selectedReel.category_data?.category_name
                                                        }
                                                        className="w-14 h-14 object-contain"
                                                    />
                                                ) : (
                                                    <div className="text-black text-xs text-center">
                                                        No image
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-xl">
                                                    {selectedReel.service_data?.service_name ||
                                                        selectedReel.category_data?.category_name}
                                                </h3>
                                                {selectedReel.service_data?.price && (
                                                    <p className="text-2xl">
                                                        ₹ {selectedReel.service_data.price}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <h3 className="font-semibold text-lg mb-2">Description</h3>
                                            <p className="text-gray-700">
                                                {selectedReel.service_data?.description?.replace(
                                                    /<[^>]*>/g,
                                                    ""
                                                ) ||
                                                    selectedReel.description ||
                                                    "No description available"}
                                            </p>
                                        </div>

                                        <div className="flex gap-4">
                                            <button className="flex-1 border border-black py-3 rounded-md">
                                                More info
                                            </button>
                                            <button
                                                onClick={() => handleBookNow(selectedReel)}
                                                className="flex-1 bg-black text-white py-3 rounded-md"
                                            >
                                                Book Now
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Product Detail Modal (Mobile) */}
            <Modal
                isOpen={showProductModal}
                onRequestClose={toggleProductModal}
                className="fixed inset-0 bg-white z-[1000]"
                overlayClassName="fixed inset-0"
            >
                <div className="flex justify-end p-4">
                    <button
                        onClick={toggleProductModal}
                        className="text-white bg-black bg-opacity-50 p-2 rounded-full"
                    >
                        <FiArrowLeft size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {productImages.length > 0 && (
                        <>
                            <div className="w-full">
                                <img
                                    src={productImages[selectedImage]}
                                    alt={`${selectedReel.service_data?.service_name ||
                                        selectedReel.category_data?.category_name
                                        }`}
                                    className="w-full h-64 object-contain mx-auto"
                                />

                                {productImages.length > 1 && (
                                    <div className="overflow-x-auto py-4">
                                        <div className="flex gap-3">
                                            {productImages.map((img, index) => (
                                                <div
                                                    key={index}
                                                    className={`shrink-0 cursor-pointer w-16 h-16 ${selectedImage === index ? "border-2 border-black" : ""
                                                        }`}
                                                    onClick={() => setSelectedImage(index)}
                                                >
                                                    <img
                                                        src={img}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="py-4 px-4">
                                <h2 className="text-xl font-bold">
                                    {selectedReel.service_data?.service_name ||
                                        selectedReel.category_data?.category_name}
                                </h2>
                                {selectedReel.service_data?.price && (
                                    <div className="text-lg font-semibold mt-1">
                                        ₹ {selectedReel.service_data.price}
                                    </div>
                                )}

                                <div className="mt-4">
                                    <h3 className="font-semibold">Description</h3>
                                    <p className="text-gray-600 mt-1 text-sm">
                                        {selectedReel.service_data?.description?.replace(
                                            /<[^>]*>/g,
                                            ""
                                        ) ||
                                            selectedReel.description ||
                                            "No description available"}
                                    </p>
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button className="flex-1 border border-black py-3 rounded-md">
                                        More info
                                    </button>
                                    <button
                                        onClick={() => handleBookNow(selectedReel)}
                                        className="flex-1 bg-black text-white py-3 rounded-md flex items-center justify-center gap-2"
                                    >
                                        Book now
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ReelCarousel;